import { payerTypeConstants } from "constants/order";
import dayjs from "dayjs";
import useFormValidation from "hooks/useFormValidation";
import { sizer } from "layout/styles/styled/sizer";
import { Label } from "layout/typography/Label";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { TabView, TabPanel } from "primereact/tabview";
import { Tooltip } from "primereact/tooltip";
import React, { useState, useMemo, useEffect } from "react";
import { Controller, FieldValues, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import { IFacilityRules, INonFacilityRules } from "types/Order/Order";

import { ValueNoteSection } from "./components/ValueNoteSection";
import { payerTypeBenefitsInfo } from "./helpers";

const StyledCard = styled(Card)<{ ordertypefield: string }>`
  margin-bottom: ${sizer(4)};
  display: ${({ ordertypefield }) =>
    ordertypefield === "GFE" ? "block" : "none"};
`;

const StyledTabView = styled(TabView)`
  .p-tabview-panels {
    padding-left: 0px !important;
  }
  .p-tabview-selected > .p-tabview-nav-link {
    background: var(--primary-10) !important;
  }
  .p-tabview-nav {
    overflow-x: auto !important;
    white-space: nowrap !important;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }
  .p-tabview-nav::-webkit-scrollbar {
    height: 4px;
  }
  .p-tabview-nav::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  .p-tabview-nav::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }
  .p-tabview-nav::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  .p-tabview-nav ul {
    flex-wrap: nowrap !important;
  }
`;

const StyledShowMore = styled.p`
  color: var(--color-purple);
`;

const StyledInputNumber = styled(InputNumber)`
  input {
    max-width: 100%;
  }
`;

const StyledIcon = styled.i`
  color: var(--primary-color);
`;

const StyledWarning = styled(Message)`
  background: transparent !important;
  flex: 1;
  min-width: 200px;

  .p-message-wrapper {
    justify-content: flex-start;
  }
`;

const MatchingOrdersList = styled.div`
  margin-top: 10px;
  padding: 12px;
  border-radius: 4px;
  background-color: var(--surface-100);
`;

const OrderItem = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--surface-200);

  &:last-child {
    border-bottom: none;
  }
`;

function MatchingOrdersDetails({
  matchingOrdersData,
  formatDate,
}: {
  matchingOrdersData: any[];
  formatDate: (date: string) => string;
}) {
  return (
    <MatchingOrdersList>
      <h3 className="mt-0 mb-2">Matching Orders</h3>
      <Divider className="my-2" />
      {matchingOrdersData.map((order: any, index: number) => (
        <OrderItem key={order.id || index}>
          <span>
            Order #
            <a
              href={`${window.location.origin}/orders/${order.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary"
            >
              {order.id}
            </a>
          </span>
          <span>Date of Service: {formatDate(order.dateOfService)}</span>
          <div className="flex flex-column">
            <span>
              Deductible Reduction: $
              {(order.deductibleReductionInCents / 100).toFixed(2)}
            </span>
            <span>
              Out-of-Pocket Reduction: $
              {(order.oopReductionInCents / 100).toFixed(2)}
            </span>
          </div>
        </OrderItem>
      ))}
    </MatchingOrdersList>
  );
}

// Optimize the formatting function
const formatCurrency = (amountInDollars: number) =>
  `$${amountInDollars.toFixed(2)}`;

// Format date string to MM/DD/YYYY
const formatDate = (dateString: string) => {
  if (!dateString) return "";
  try {
    return dayjs(dateString).format("MM/DD/YYYY");
  } catch (e) {
    return dateString;
  }
};

const ActionButton = styled(Button)`
  height: 2.25rem !important;
  white-space: nowrap;

  /* Ensure consistent height on smaller screens */
  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

export function PatientBenefitsForm({
  account,
  draft,
  redo,
  copy,
  costApplicationRules,
}: {
  account: IAccount;
  draft?: boolean;
  redo?: boolean;
  copy?: boolean;
  costApplicationRules?: {
    facilityRules: IFacilityRules;
    nonFacilityRules: INonFacilityRules;
  };
}) {
  const [showMore, setShowMore] = useState(true);
  const [hideBenefits, setHideBenefits] = useState(false);
  const [showFacilityBenefits, setShowFacilityBenefits] = useState(
    account.costApplicationRulesVisible.facilityRules
  );
  const [showProfessionalBenefits, setShowProfessionalBenefits] = useState(
    account.costApplicationRulesVisible.nonFacilityRules
  );
  const [showMatchingOrders, setShowMatchingOrders] = useState(false);
  const [showProfessionalCopay, setShowProfessionalCopay] = useState(false);

  const { watch, control, trigger, setValue, getValues } =
    useFormContext<FieldValues>();

  // Progressive deductible state
  const [reductionsApplied, setReductionsApplied] = useState(false);
  const [prevDeduct, setPrevDeduct] = useState<number>(0);
  const [prevOop, setPrevOop] = useState<number>(0);

  const orderTypeField = watch("orderType");
  const benefitDetailsField = watch("benefitDetails");
  const payerNameField = watch("payerName");
  const copayField = watch("flatCopay");
  const professionalFlatCopayField = watch("professionalFlatCopay");
  const draftOrderFirstRenderField = watch("draftOrderFirstRender");
  const redoOrderFirstRenderField = watch("redoOrderFirstRender");
  const copyOrderFirstRenderField = watch("copyOrderFirstRender");

  // Fields for reduction functionality
  const deductionSuggested = watch("deductibleReductionAmount") || 0;
  const oopSuggested = watch("oopReductionAmount") || 0;
  const hasMatchingOrders = watch("hasMatchingOrders") || false;
  const matchingOrdersData = watch("matchingOrdersData") || [];

  const { handleOnBlurField } = useFormValidation();
  const { payersTypeInfo } = useSelector((state: any) => state.orderSlice);

  // Apply deductible and OOP reductions
  const applyReductions = () => {
    const currentDeductible = getValues("deductible") || 0;
    const currentOop = getValues("outOfPocketMax") || 0;

    // Store original values for potential undo
    setPrevDeduct(currentDeductible);
    setPrevOop(currentOop);

    // Apply reductions
    setValue("deductible", Math.max(0, currentDeductible - deductionSuggested));
    setValue("outOfPocketMax", Math.max(0, currentOop - oopSuggested));

    // Mark as applied
    setReductionsApplied(true);
    setValue("reductionsApplied", true);

    // Validate fields after changes
    trigger(["deductible", "outOfPocketMax"]);
  };

  // Restore original values
  const undoReductions = () => {
    setValue("deductible", prevDeduct);
    setValue("outOfPocketMax", prevOop);
    setReductionsApplied(false);
    setValue("reductionsApplied", false);
    trigger(["deductible", "outOfPocketMax"]);
  };

  // Reset reduction applied state when new suggestions arrive
  useEffect(() => {
    if (reductionsApplied && (deductionSuggested > 0 || oopSuggested > 0)) {
      setReductionsApplied(false);
      setValue("reductionsApplied", false);
    }
  }, [deductionSuggested, oopSuggested]);

  // Handle professional copay automation from eligibility response
  useEffect(() => {
    const handleProfessionalCopayTrigger = (event: CustomEvent) => {
      // Show professional copay field if API returns a value > 0, regardless of Non-facility Rules setting
      if (event.detail?.value > 0) {
        setShowProfessionalCopay(true);
        // Only uncheck professional copay if professional benefits are enabled
        if (showProfessionalBenefits) {
          setValue("professionalCopay", false);
          trigger("professionalCopay");
        }
      }
    };

    window.addEventListener(
      "triggerProfessionalCopay",
      handleProfessionalCopayTrigger as EventListener
    );
    return () => {
      window.removeEventListener(
        "triggerProfessionalCopay",
        handleProfessionalCopayTrigger as EventListener
      );
    };
  }, [showProfessionalBenefits, setValue, trigger]);

  useEffect(() => {
    const facilityRulesVisibleForAccount =
      account.costApplicationRulesVisible.facilityRules;
    const nonFacilityRulesVisibleForAccount =
      account.costApplicationRulesVisible.nonFacilityRules;
    const payerType = payersTypeInfo.find(
      (payer: { name: string; type: string }) => payer.name === payerNameField
    )?.type;
    if (!payerType) return;
    let fieldValues = {
      facilityCopay: true,
      facilityDeductible: true,
      facilityCoinsurance: true,
      professionalCopay: true,
      professionalDeductible: true,
      professionalCoinsurance: true,
    };

    if (payerNameField === "Self-Pay Rate") {
      setShowFacilityBenefits(false);
      setShowProfessionalBenefits(false);
      setHideBenefits(true);
    } else {
      setShowFacilityBenefits(facilityRulesVisibleForAccount);
      setShowProfessionalBenefits(nonFacilityRulesVisibleForAccount);
      setHideBenefits(false);
      if (
        ((draft && draftOrderFirstRenderField) ||
          (redo && redoOrderFirstRenderField) ||
          (copy && copyOrderFirstRenderField)) &&
        !!costApplicationRules
      ) {
        if (!facilityRulesVisibleForAccount) {
          setValue("facilityCopay", true);
          setValue("facilityDeductible", true);
          setValue("facilityCoinsurance", true);
        }
        if (!nonFacilityRulesVisibleForAccount) {
          setValue("professionalCopay", true);
          setValue("professionalDeductible", true);
          setValue("professionalCoinsurance", true);
        }
        return;
      }
      if (
        ((draft && draftOrderFirstRenderField) ||
          (redo && redoOrderFirstRenderField) ||
          (copy && copyOrderFirstRenderField)) &&
        !costApplicationRules
      ) {
        Object.entries(fieldValues).forEach(([field, value]) => {
          setValue(field, value);
        });
        return;
      }
      if (payerType === payerTypeConstants.MEDICARE_ADVANTAGE) {
        fieldValues = {
          ...fieldValues,
          facilityDeductible: true,
          facilityCoinsurance: facilityRulesVisibleForAccount
            ? !(copayField > 0)
            : true,
          professionalDeductible: true,
          professionalCoinsurance: nonFacilityRulesVisibleForAccount
            ? !(copayField > 0)
            : true,
        };
      }
    }

    Object.entries(fieldValues).forEach(([field, value]) => {
      setValue(field, value);
    });
  }, [payerNameField, copayField, payersTypeInfo, draft, copy, redo]);

  const eligibilityDetailsField = useMemo(() => {
    return benefitDetailsField?.response.details;
  }, [benefitDetailsField]);

  const allDeductibleFieldsNull = useMemo(() => {
    if (!benefitDetailsField) return false;
    return !!benefitDetailsField?.response?.isRemainingDeductibleEmpty;
  }, [benefitDetailsField]);

  const coinsuranceFieldIsNull = useMemo(() => {
    if (!benefitDetailsField) return false;
    return !!benefitDetailsField?.response?.isCoinsurancePercentEmpty;
  }, [benefitDetailsField]);

  const planName = useMemo(() => {
    const planName = {
      Value: benefitDetailsField?.response?.planCoverageSummary?.PlanName,
    };
    return [planName].filter((value) => value !== null && value !== undefined);
  }, [benefitDetailsField]);

  const policyType = useMemo(() => {
    const policyType = {
      Value: benefitDetailsField?.response?.planCoverageSummary?.PolicyType,
    };
    return [policyType].filter(
      (value) => value !== null && value !== undefined
    );
  }, [benefitDetailsField]);

  const individualDeductible = useMemo(() => {
    const individualDeductibleRemainingInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.IndividualDeductibleRemainingInNet;
    return [individualDeductibleRemainingInNet].filter(
      (value) => value !== null && value !== undefined
    );
  }, [eligibilityDetailsField]);

  const familyDeductible = useMemo(() => {
    const familyRemainingDeductibleInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.FamilyDeductibleRemainingInNet;
    return [familyRemainingDeductibleInNet].filter(
      (value) => value !== null && value !== undefined
    );
  }, [eligibilityDetailsField]);

  const individualCalendarYearDeductible = useMemo(() => {
    const value =
      benefitDetailsField?.response?.individualCalendarYearDeductible;
    if (value === null || value === undefined) return [];
    return [{ Value: `$${value.toFixed(2)}` }];
  }, [benefitDetailsField]);

  const familyCalendarYearDeductible = useMemo(() => {
    const value = benefitDetailsField?.response?.familyCalendarYearDeductible;
    if (value === null || value === undefined) return [];
    return [{ Value: `$${value.toFixed(2)}` }];
  }, [benefitDetailsField]);

  const individualOOP = useMemo(() => {
    const individualRemainingOOPInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.IndividualOOPRemainingInNet;
    return [individualRemainingOOPInNet].filter(
      (value) =>
        value !== null &&
        value !== undefined &&
        value.Value !== "$0.00" &&
        value.Value !== "$0.0" &&
        value.Value !== "$0"
    );
  }, [eligibilityDetailsField]);

  const familyOOP = useMemo(() => {
    const familyRemainingOOPInNet =
      eligibilityDetailsField?.HBPC_Deductible_OOP_Summary
        ?.FamilyOOPRemainingInNet;
    return [familyRemainingOOPInNet].filter(
      (value) =>
        value !== null &&
        value !== undefined &&
        value.Value !== "$0.00" &&
        value.Value !== "$0.0" &&
        value.Value !== "$0"
    );
  }, [eligibilityDetailsField]);

  const specialitOfficeBenefits = useMemo(() => {
    const specialistOfficeSummaryCoPay =
      eligibilityDetailsField?.SpecialistOfficeSummary?.CoPayInNet;
    const specialistOfficeSummaryCoins =
      eligibilityDetailsField?.SpecialistOfficeSummary?.CoInsInNet;
    const individualDeductible = eligibilityDetailsField
      ?.SpecialistOfficeSummary?.IndividualDeductibleRemainingInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.SpecialistOfficeSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.SpecialistOfficeSummary?.serviceDetails
        ?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: specialistOfficeSummaryCoPay?.Value,
      Notes: specialistOfficeSummaryCoPay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: specialistOfficeSummaryCoins?.Value,
      Notes: specialistOfficeSummaryCoins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      specialistOfficeSummaryCoPay || copayFromServiceDetails
        ? copayDetails
        : null;
    const item2 =
      specialistOfficeSummaryCoins || coinsuranceFromServiceDetails?.length
        ? coinsDetails
        : null;
    const item3 = individualDeductible
      ? { subtitle: "Remaining Deductible", ...individualDeductible }
      : null;
    return [item1, item2, item3].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const ascSummary = useMemo(() => {
    const copay = eligibilityDetailsField?.ASC_FacilitySummary?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.ASC_FacilitySummary?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copaysFromServiceDetails =
      eligibilityDetailsField?.ASC_FacilitySummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.ASC_FacilitySummary?.serviceDetails?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copaysFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copaysFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const hospitalInpatientSummary = useMemo(() => {
    const copay = eligibilityDetailsField?.HospitalInpatientSummary
      ?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.HospitalInpatientSummary
      ?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.HospitalInpatientSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.HospitalInpatientSummary?.serviceDetails
        ?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copayFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const hospitalOutpatientBenefits = useMemo(() => {
    const copay = eligibilityDetailsField?.HospitalOutPatientSummary
      ?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.HospitalOutPatientSummary
      ?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copayFromServiceDetails =
      eligibilityDetailsField?.HospitalOutPatientSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.HospitalOutPatientSummary?.serviceDetails
        ?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copayFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };

    const item1 =
      copay || copayFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const mriCtSummary = useMemo(() => {
    const copay = eligibilityDetailsField?.MRI_CT_ScanSummary?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.MRI_CT_ScanSummary?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copaysFromServiceDetails =
      eligibilityDetailsField?.MRI_CT_ScanSummary?.serviceDetails?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.MRI_CT_ScanSummary?.serviceDetails?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copaysFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copaysFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const physicianVisitOutpatientBenefits = useMemo(() => {
    const copay = eligibilityDetailsField?.PhysicianVisitOutpatientSummary
      ?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.PhysicianVisitOutpatientSummary
      ?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const copaysFromServiceDetails =
      eligibilityDetailsField?.PhysicianVisitOutpatientSummary?.serviceDetails
        ?.copay;
    const coinsuranceFromServiceDetails =
      eligibilityDetailsField?.PhysicianVisitOutpatientSummary?.serviceDetails
        ?.coinsurance;
    const copayDetails = {
      subtitle: "Copay",
      Value: copay?.Value,
      Notes: copay?.Notes,
      additionalCopay: copaysFromServiceDetails,
    };
    const coinsDetails = {
      subtitle: "Coinsurance",
      Value: coins?.Value,
      Notes: coins?.Notes,
      additionalCoinsurance: coinsuranceFromServiceDetails,
    };
    const item1 =
      copay || copaysFromServiceDetails?.length ? copayDetails : null;
    const item2 =
      coins || coinsuranceFromServiceDetails?.length ? coinsDetails : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const xRaySummary = useMemo(() => {
    const copay = eligibilityDetailsField?.Diagnostic_X_Ray?.CoPayInNet as {
      Value: string;
      Notes: string;
    } | null;
    const coins = eligibilityDetailsField?.Diagnostic_X_Ray?.CoInsInNet as {
      Value: string;
      Notes: string;
    } | null;
    const item1 = copay ? { subtitle: "Copay", ...copay } : null;
    const item2 = coins ? { subtitle: "Coinsurance", ...coins } : null;
    return [item1, item2].filter(
      (value) => value !== null && value !== undefined
    ) as any[];
  }, [eligibilityDetailsField]);

  const showAdditionalDetailsSection = useMemo(() => {
    const hasData =
      individualDeductible.length > 0 ||
      familyDeductible.length > 0 ||
      individualOOP.length > 0 ||
      familyOOP.length > 0 ||
      individualCalendarYearDeductible.length > 0 ||
      familyCalendarYearDeductible.length > 0 ||
      specialitOfficeBenefits.length > 0 ||
      ascSummary.length > 0 ||
      hospitalInpatientSummary.length > 0 ||
      mriCtSummary.length > 0 ||
      hospitalOutpatientBenefits.length > 0 ||
      physicianVisitOutpatientBenefits.length > 0;
    return eligibilityDetailsField && hasData;
  }, [
    individualDeductible,
    familyDeductible,
    individualOOP,
    familyOOP,
    individualCalendarYearDeductible,
    familyCalendarYearDeductible,
    eligibilityDetailsField,
    specialitOfficeBenefits,
    ascSummary,
    hospitalInpatientSummary,
    mriCtSummary,
    hospitalOutpatientBenefits,
    physicianVisitOutpatientBenefits,
  ]);

  const serviceCoverage = useMemo(() => {
    return benefitDetailsField?.response?.serviceCoverage || {};
  }, [benefitDetailsField]);

  const tabs = useMemo(() => {
    return [
      {
        title: "Hospital Outpatient",
        items: hospitalOutpatientBenefits,
        serviceCoverage: serviceCoverage?.hospitalOutpatient,
      },
      {
        title: "ASC Facility",
        items: ascSummary,
        serviceCoverage: serviceCoverage?.ascFacility,
      },
      {
        title: "Hospital Inpatient",
        items: hospitalInpatientSummary,
        serviceCoverage: serviceCoverage?.hospitalInpatient,
      },
      {
        title: "Specialist Office",
        items: specialitOfficeBenefits,
        serviceCoverage: serviceCoverage?.specialistOffice,
      },
      {
        title: "Physician Outpatient",
        items: physicianVisitOutpatientBenefits,
        serviceCoverage: serviceCoverage?.physicianVisitOutpatient,
      },
      {
        title: "MRI/CT Scan Benefits",
        items: mriCtSummary,
        serviceCoverage: serviceCoverage?.mriCt,
      },
      {
        title: "Diagnostic X-Ray",
        items: xRaySummary,
      },
    ].filter((tab) => tab.items.length > 0);
  }, [
    hospitalOutpatientBenefits,
    ascSummary,
    hospitalInpatientSummary,
    specialitOfficeBenefits,
    physicianVisitOutpatientBenefits,
    mriCtSummary,
    xRaySummary,
    serviceCoverage,
  ]);

  // Get count of matching orders
  const orderCount = matchingOrdersData.length;

  // Simplify the getReductionText function
  const getReductionText = () => {
    if (deductionSuggested > 0 && oopSuggested > 0) {
      return (
        `Reduce deductible by $${deductionSuggested.toFixed(2)}` +
        ` and out-of-pocket max by $${oopSuggested.toFixed(2)}`
      );
    }
    return `Reduce deductible by $${deductionSuggested.toFixed(2)}`;
  };

  if (hideBenefits) {
    return null;
  }
  return (
    <StyledCard
      title="Automatically Selected Benefit (Please Verify)"
      ordertypefield={orderTypeField}
    >
      <div className="flex flex-column w-100">
        <div className="formgrid grid w-100">
          <div className="field flex flex-column col-12 md:col-12 lg:col-3">
            <Label htmlFor="deductible">Remaining Deductible</Label>

            <Controller
              name="deductible"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="flex gap-1 w-full align-items-center">
                  <StyledInputNumber
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "deductible",
                        trigger,
                      })
                    }
                    inputId="deductible"
                    onValueChange={({ value }) => onChange(value)}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    aria-autocomplete="none"
                    className="w-full"
                  />
                  {allDeductibleFieldsNull && (
                    <>
                      <Tooltip
                        target=".info-icon"
                        style={{
                          fontSize: "14px",
                          width: "250px",
                        }}
                      />
                      <i
                        className="pi pi-info-circle info-icon"
                        data-pr-tooltip="Deductible info is missing. Please confirm with the payer that this plan has no deductible."
                        style={{ color: "var(--red-600)" }}
                      />
                    </>
                  )}
                </div>
              )}
            />
          </div>

          <div className="field flex flex-column col-12 md:col-12 lg:col-3">
            <Label htmlFor="outOfPocketMax">Remaining Out-of-pocket Max</Label>

            <Controller
              name="outOfPocketMax"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <InputNumber
                  value={value}
                  currency="USD"
                  locale="en-US"
                  mode="currency"
                  onBlur={() =>
                    handleOnBlurField({
                      onBlur,
                      field: "outOfPocketMax",
                      trigger,
                    })
                  }
                  inputId="outOfPocketMax"
                  onValueChange={({ value }) => onChange(value)}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  aria-autocomplete="none"
                />
              )}
            />
          </div>
          <div className="field flex flex-column col-12 md:col-12 lg:col-3">
            <div className="flex align-items-center gap-2">
              <Label htmlFor="flatCopay">Flat Copay</Label>
              {showProfessionalBenefits && (
                <button
                  type="button"
                  className="text-sm text-primary-600 no-underline border-none bg-transparent cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    setShowProfessionalCopay(!showProfessionalCopay);
                    setValue("professionalCopay", false);
                    trigger("professionalCopay");
                  }}
                >
                  {showProfessionalCopay ||
                  (professionalFlatCopayField && professionalFlatCopayField > 0)
                    ? "-Prof Copay"
                    : "+Prof Copay"}
                </button>
              )}
            </div>
            <Controller
              name="flatCopay"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <StyledInputNumber
                  value={value}
                  currency="USD"
                  locale="en-US"
                  mode="currency"
                  onBlur={() =>
                    handleOnBlurField({
                      onBlur,
                      field: "flatCopay",
                      trigger,
                    })
                  }
                  inputId="flatCopay"
                  onValueChange={({ value }) => {
                    if (redo) {
                      setValue("redoOrderFirstRender", false);
                    }
                    if (draft) {
                      setValue("draftOrderFirstRender", false);
                    }
                    if (copy) {
                      setValue("copyOrderFirstRender", false);
                    }
                    onChange(value);
                  }}
                  minFractionDigits={2}
                  maxFractionDigits={2}
                  min={0}
                  aria-autocomplete="none"
                  className="w-full"
                />
              )}
            />
            {(showProfessionalCopay ||
              (professionalFlatCopayField &&
                professionalFlatCopayField > 0)) && (
              <div className="mt-2">
                <Label htmlFor="professionalFlatCopay">
                  Professional Copay
                </Label>
                <Controller
                  name="professionalFlatCopay"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <StyledInputNumber
                      value={value}
                      currency="USD"
                      locale="en-US"
                      mode="currency"
                      onBlur={() =>
                        handleOnBlurField({
                          onBlur,
                          field: "professionalFlatCopay",
                          trigger,
                        })
                      }
                      inputId="professionalFlatCopay"
                      onValueChange={({ value }) => {
                        if (redo) {
                          setValue("redoOrderFirstRender", false);
                        }
                        if (draft) {
                          setValue("draftOrderFirstRender", false);
                        }
                        if (copy) {
                          setValue("copyOrderFirstRender", false);
                        }
                        onChange(value);
                      }}
                      minFractionDigits={2}
                      maxFractionDigits={2}
                      min={0}
                      aria-autocomplete="none"
                      className="w-full"
                    />
                  )}
                />
              </div>
            )}
          </div>
          <div className="field flex flex-column col-12 md:col-12 lg:col-3">
            <Label htmlFor="coinsurancePercent">Coinsurance Percent</Label>

            <Controller
              name="coinsurancePercent"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="flex gap-1 w-full align-items-center">
                  <InputNumber
                    value={value}
                    suffix="%"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "coinsurancePercent",
                        trigger,
                      })
                    }
                    inputId="coinsurancePercent"
                    onValueChange={({ value }) => onChange(value)}
                    min={0}
                    max={100}
                    aria-autocomplete="none"
                    className="w-full"
                  />
                  {coinsuranceFieldIsNull && (
                    <>
                      <Tooltip
                        target=".info-icon"
                        style={{
                          fontSize: "14px",
                          width: "250px",
                        }}
                      />
                      <i
                        className="pi pi-info-circle info-icon"
                        data-pr-tooltip="Coinsurance info is missing. Please confirm with the payer that this plan has no coinsurance."
                        style={{ color: "var(--red-600)" }}
                      />
                    </>
                  )}
                </div>
              )}
            />
          </div>
        </div>

        {/* Progressive deductible UI with matching orders info */}
        {hasMatchingOrders && (
          <div className="mt-3 mb-2">
            <div className="flex align-items-center flex-wrap gap-2">
              <StyledWarning
                severity={reductionsApplied ? "success" : "warn"}
                text={
                  reductionsApplied
                    ? `Successfully applied reductions to benefits`
                    : `Found ${orderCount} previous order${
                        orderCount !== 1 ? "s" : ""
                      } for this patient. ${getReductionText()}`
                }
              />
              <div className="flex gap-2">
                <ActionButton
                  label={reductionsApplied ? "Undo" : "Apply"}
                  className="p-button-sm"
                  onClick={reductionsApplied ? undoReductions : applyReductions}
                />
                {orderCount > 0 && (
                  <ActionButton
                    label={showMatchingOrders ? "Hide Details" : "Show Details"}
                    className="p-button-sm p-button-outlined"
                    onClick={() => setShowMatchingOrders(!showMatchingOrders)}
                    icon={
                      showMatchingOrders
                        ? "pi pi-chevron-up"
                        : "pi pi-chevron-down"
                    }
                  />
                )}
              </div>
            </div>

            {/* Matching orders details section */}
            {showMatchingOrders && matchingOrdersData.length > 0 && (
              <MatchingOrdersDetails
                matchingOrdersData={matchingOrdersData}
                formatDate={formatDate}
              />
            )}
          </div>
        )}

        <div className="w-full formgrid grid">
          {showFacilityBenefits && (
            <div className="flex flex-column gap-2 field col-12 md:col-6 lg:col-6">
              <p className="text-lg font-bold my-2">Facility Benefits</p>
              <div className="formgrid grid">
                <div className="col-12 md:col-6 lg:col-3 mb-2">
                  <Controller
                    name="facilityCopay"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex gap-2 align-items-center">
                        <Checkbox
                          className="ml-3"
                          onChange={onChange}
                          checked={value}
                          inputId="facilityCopay"
                          name="facilityCopay"
                        />
                        <Label htmlFor="facilityCopay">Copay</Label>
                      </div>
                    )}
                  />
                </div>
                <div className="col-12 md:col-6 lg:col-3 mb-2">
                  <Controller
                    name="facilityDeductible"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex gap-2 align-items-center">
                        <Checkbox
                          className="ml-3"
                          onChange={onChange}
                          checked={value}
                          inputId="facilityDeductible"
                          name="facilityDeductible"
                        />
                        <Label htmlFor="facilityDeductible">Deductible</Label>
                      </div>
                    )}
                  />
                </div>
                <div className="col-12 md:col-6 lg:col-3">
                  <Controller
                    name="facilityCoinsurance"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex gap-2 align-items-center">
                        <Checkbox
                          className="ml-3"
                          onChange={onChange}
                          checked={value}
                          inputId="facilityCoinsurance"
                          name="facilityCoinsurance"
                        />
                        <Label htmlFor="facilityCoinsurance">Coinsurance</Label>
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          )}
          {showProfessionalBenefits && (
            <div className="flex flex-column gap-2 field col-12 md:col-6 lg:col-6">
              <p className="text-lg font-bold my-2">Professional Benefits</p>
              <div className="formgrid grid">
                <div className="col-12 md:col-6 lg:col-3 mb-2">
                  <Controller
                    name="professionalCopay"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex gap-2 align-items-center">
                        <Checkbox
                          className="ml-3"
                          onChange={onChange}
                          checked={value}
                          inputId="professionalCopay"
                          name="professionalCopay"
                        />
                        <Label htmlFor="professionalCopay">Copay</Label>
                      </div>
                    )}
                  />
                </div>
                <div className="col-12 md:col-6 lg:col-3 mb-2">
                  <Controller
                    name="professionalDeductible"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex gap-2 align-items-center">
                        <Checkbox
                          className="ml-3"
                          onChange={onChange}
                          checked={value}
                          inputId="professionalDeductible"
                          name="professionalDeductible"
                        />
                        <Label htmlFor="professionalDeductible">
                          Deductible
                        </Label>
                      </div>
                    )}
                  />
                </div>
                <div className="col-12 md:col-6 lg:col-3">
                  <Controller
                    name="professionalCoinsurance"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <div className="flex gap-2 align-items-center">
                        <Checkbox
                          className="ml-3"
                          onChange={onChange}
                          checked={value}
                          inputId="professionalCoinsurance"
                          name="professionalCoinsurance"
                        />
                        <Label htmlFor="professionalCoinsurance">
                          Coinsurance
                        </Label>
                      </div>
                    )}
                  />
                </div>
              </div>
            </div>
          )}
          {(showFacilityBenefits || showProfessionalBenefits) && (
            <div className="flex flex-column field col-12 mt-4">
              {payerTypeBenefitsInfo.map((item, index) => (
                <div className="flex gap-2" key={`${item.title}_${index}`}>
                  <StyledIcon className="pi pi-sun" />
                  <p className="font-normal italic">
                    <span className="font-bold text-primary-700">
                      {item.title}:
                    </span>{" "}
                    {item.info}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
        {showAdditionalDetailsSection && (
          <div className="w-full flex flex-column">
            <StyledShowMore
              className="small-text purple flex align-items-center cursor-pointer mb-4"
              onClick={() => setShowMore(!showMore)}
            >
              {showMore ? (
                <>
                  Hide benefit details <i className="pi pi-angle-up ml-1" />
                </>
              ) : (
                <>
                  Review All Benefits <i className="pi pi-angle-down ml-1" />
                </>
              )}
            </StyledShowMore>
            {showMore && (
              <div className="card">
                <StyledTabView>
                  <TabPanel header="Plan Deductible/OOP">
                    <ValueNoteSection title="Plan Name" items={planName} />
                    <ValueNoteSection title="Policy Type" items={policyType} />
                    <ValueNoteSection
                      title="Individual Calendar Year Deductible"
                      items={individualCalendarYearDeductible}
                    />
                    <ValueNoteSection
                      title="Family Calendar Year Deductible"
                      items={familyCalendarYearDeductible}
                    />
                    <ValueNoteSection
                      title="Remaining Individual Deductible"
                      items={individualDeductible}
                    />
                    <ValueNoteSection
                      title="Remaining Family Deductible"
                      items={familyDeductible}
                    />
                    <ValueNoteSection
                      title="Remaining Individual OOP"
                      items={individualOOP}
                    />
                    <ValueNoteSection
                      title="Remaining Family OOP"
                      items={familyOOP}
                    />
                  </TabPanel>
                  {tabs.map((tab: any) => {
                    return (
                      <TabPanel key={tab.title} header={tab.title}>
                        <ValueNoteSection
                          title={tab.title}
                          items={tab.items}
                          serviceCoverage={tab.serviceCoverage}
                        />
                      </TabPanel>
                    );
                  })}
                </StyledTabView>
              </div>
            )}
          </div>
        )}
      </div>
    </StyledCard>
  );
}
